import { ResponseResolver, MockedRequest, restContext, RestRequest } from 'msw';
import { delayedResponse } from '@/utils/useMswCustomResponse';
import { defaultMapCenter } from '@/config/maps';
import { choiceRandom } from '@/utils/useHelper';
import type {
  CitiesDetailPerPage,
  Garage,
  Pass,
  Pagination,
  Parking,
  ParkingDetail,
  PrefectureDetail,
  Region,
  Usage,
  ParkingType,
} from '../api/types';

const getAllPrefectures: ResponseResolver<MockedRequest, typeof restContext> = (
  req,
  res,
  ctx,
) => {
  return delayedResponse(
    ctx.status(200),
    ctx.json<{ regions: Region[] }>({
      regions: [
        {
          name: '北海道',
          prefectures: [
            {
              id: 1,
              name: '北海道',
            },
          ],
        },
        {
          name: '東北',
          prefectures: [
            {
              id: 2,
              name: '青森県',
            },
            {
              id: 3,
              name: '岩手県',
            },
          ],
        },
        {
          name: '関東',
          prefectures: [
            {
              id: 8,
              name: '神奈川県',
            },
            {
              id: 9,
              name: '栃木県',
            },
            {
              id: 10,
              name: '東京都',
            },
            {
              id: 11,
              name: '埼玉県',
            },
          ],
        },
        {
          name: '北陸・中部',
          prefectures: [
            {
              id: 17,
              name: '石川県',
            },
          ],
        },
        {
          name: '関西',
          prefectures: [
            {
              id: 27,
              name: '大阪府',
            },
          ],
        },
        {
          name: '四国・中国',
          prefectures: [
            {
              id: 37,
              name: '香川県',
            },
          ],
        },
        {
          name: '九州・沖縄',
          prefectures: [
            {
              id: 47,
              name: '沖縄県',
            },
          ],
        },
      ],
    }),
  );
};

const getAllCitiesInThePrefecture: ResponseResolver<
  MockedRequest,
  typeof restContext
> = (req, res, ctx) => {
  return delayedResponse(
    ctx.status(200),
    ctx.json<PrefectureDetail>({
      prefecture: {
        id: 10,
        name: '東京都',
      },
      columns: [
        {
          name: 'ア行',
          cities: [
            {
              id: 110,
              name: 'あきる野市',
            },
            {
              id: 111,
              name: 'いきる野市',
            },
            {
              id: 112,
              name: 'うきる野市',
            },
            {
              id: 113,
              name: 'えきる野市',
            },
            {
              id: 114,
              name: 'おきる野市',
            },
          ],
        },
        {
          name: 'カ行',
          cities: [
            {
              id: 210,
              name: 'かきる野市',
            },
            {
              id: 211,
              name: 'ききる野市',
            },
            {
              id: 212,
              name: 'くきる野市',
            },
            {
              id: 213,
              name: 'けきる野市',
            },
            {
              id: 214,
              name: 'こきる野市',
            },
          ],
        },
        {
          name: 'サ行',
          cities: [
            {
              id: 310,
              name: 'さきる野市',
            },
            {
              id: 311,
              name: 'しきる野市',
            },
            {
              id: 312,
              name: 'すきる野市',
            },
            {
              id: 313,
              name: 'せきる野市',
            },
            {
              id: 314,
              name: 'そきる野市',
            },
          ],
        },
        {
          name: 'マ行',
          cities: [
            {
              id: 710,
              name: 'まきる野市',
            },
            {
              id: 711,
              name: 'みきる野市',
            },
            {
              id: 712,
              name: 'むきる野市',
            },
            {
              id: 712,
              name: 'めきる野市',
            },
            {
              id: 712,
              name: 'もきる野市',
            },
          ],
        },
      ],
    }),
  );
};

const getAllParkingsInTheCity: ResponseResolver<
  RestRequest,
  typeof restContext
> = (req, res, ctx) => {
  const paramPage = new URL(req.url.href).searchParams.get('page');

  const pages = [...Array(6).keys()].map((i) => ({
    total: 55,
    currentPage: i + 1,
    perPage: 10,
    lastPage: 6,
    firstPageUrl: 'http://localhost:3333/users/10/payments?page=1',
    lastPageUrl: 'http://localhost:3333/users/10/payments?page=6',
    prevPageUrl: `http://localhost:3333/users/10/payments?page=${i}`,
    nextPageUrl: `http://localhost:3333/users/10/payments?page=${i + 2}`,
  }));
  const currentPagePoint = Number(paramPage || 1);
  const targetPage = pages.find(
    (page) => page.currentPage === currentPagePoint,
  );

  const _parkings = [...Array(55).keys()].map((i) => {
    return {
      id: i + 1,
      name: `ID:${i + 1} 新宿若葉第${i + 1}`,
      address: '東京都新宿区１丁目４−１',
      isAppPaymentSupported: i % 2 === 0,
      tags: [{ name: 'ロック板式' }, { name: 'ゲート式' }],
      sectionCount: 10,
      otherSectionCount: 5,
      monthlySectionCount: choiceRandom([5, null]),
      latitude:
        i % 2
          ? defaultMapCenter.lat - 0.01 * i
          : defaultMapCenter.lat + 0.01 * i,
      longitude:
        i % 2
          ? defaultMapCenter.lng - 0.01 * i
          : defaultMapCenter.lng + 0.01 * i,
      hasAvailableRentalGarage: i % 3 === 0,
      isDummy: i % 5 === 0,
      priceSystems: [
        {
          title: '全曜日',
          timeZone: '8:00 ~ 20:00',
          unit: '30分',
          price: '200円',
          remarks: 'オールタイム',
        },
        {
          title: '全曜日',
          timeZone: '8:00 ~ 20:00',
          unit: '30分',
          price: '200円',
          remarks: '5番車室のみ',
        },
        {
          title: '24時間最大（日・祝）',
          timeZone: '8:00 ~ 20:00',
          unit: '', // unitは空文字の場合もある
          price: '200円',
          remarks: '15.16番車室のみ',
        },
      ],
    };
  });

  const paginationLimit = Number(req.url.searchParams.get('limit')) || 10;
  const pageLength = Math.ceil(_parkings.length / paginationLimit);

  const slicedParkings = [...Array(pageLength).keys()].map((i) =>
    _parkings.slice(i * paginationLimit, (i + 1) * paginationLimit),
  );

  return delayedResponse(
    ctx.status(200),
    ctx.json<CitiesDetailPerPage>({
      parkings: slicedParkings[Number(paramPage || 1) - 1],
      pagination: targetPage || pages[0],
    }),
  );
};

const searchByKeyword: ResponseResolver<MockedRequest, typeof restContext> = (
  req,
  res,
  ctx,
) => {
  const paramPage = new URL(req.url.href).searchParams.get('page');

  const pages = [...Array(6).keys()].map((i) => ({
    total: 55,
    currentPage: i + 1,
    perPage: 10,
    lastPage: 6,
    firstPageUrl: 'http://localhost:3333/users/10/payments?page=1',
    lastPageUrl: 'http://localhost:3333/users/10/payments?page=6',
    prevPageUrl: `http://localhost:3333/users/10/payments?page=${i}`,
    nextPageUrl: `http://localhost:3333/users/10/payments?page=${i + 2}`,
  }));
  const currentPagePoint = Number(paramPage || 1);
  const targetPage = pages.find(
    (page) => page.currentPage === currentPagePoint,
  );

  const _parkings = [...Array(55).keys()].map((i) => {
    return {
      id: i + 1,
      name: `ID:${i + 1} 新宿若葉第${i + 1}`,
      address: '東京都新宿区１丁目４−１',
      isAppPaymentSupported: i % 2 === 0,
      tags: [{ name: 'ロック板式' }, { name: 'ゲート式' }],
      sectionCount: 10,
      otherSectionCount: 5,
      monthlySectionCount: choiceRandom([5, null]),
      latitude:
        i % 2
          ? defaultMapCenter.lat - 0.01 * i
          : defaultMapCenter.lat + 0.01 * i,
      longitude:
        i % 2
          ? defaultMapCenter.lng - 0.01 * i
          : defaultMapCenter.lng + 0.01 * i,
      hasAvailableRentalGarage: i % 3 === 0,
      isDummy: i % 5 === 0,
      priceSystems: [
        {
          title: '全曜日',
          timeZone: '8:00 ~ 20:00',
          unit: '30分',
          price: '200円',
          remarks: 'オールタイム',
        },
        {
          title: '全曜日',
          timeZone: '8:00 ~ 20:00',
          unit: '30分',
          price: '200円',
          remarks: '5番車室のみ',
        },
        {
          title: '24時間最大（日・祝）',
          timeZone: '8:00 ~ 20:00',
          unit: '30分',
          price: '200円',
          remarks: '15.16番車室のみ',
        },
      ],
    };
  });

  const paginationLimit = Number(req.url.searchParams.get('limit')) || 10;
  const pageLength = Math.ceil(_parkings.length / paginationLimit);

  const slicedParkings = [...Array(pageLength).keys()].map((i) =>
    _parkings.slice(i * paginationLimit, (i + 1) * paginationLimit),
  );

  return delayedResponse(
    ctx.status(200),
    ctx.json<{ parkings: Parking[]; pagination: Pagination }>({
      parkings: slicedParkings[Number(paramPage || 1) - 1],
      pagination: targetPage || pages[0],
    }),
  );
};

const searchByLocation: ResponseResolver<MockedRequest, typeof restContext> = (
  req,
  res,
  ctx,
) => {
  const paramPage = new URL(req.url.href).searchParams.get('page');

  const pages = [...Array(6).keys()].map((i) => ({
    total: 55,
    currentPage: i + 1,
    perPage: 10,
    lastPage: 6,
    firstPageUrl: 'http://localhost:3333/users/10/payments?page=1',
    lastPageUrl: 'http://localhost:3333/users/10/payments?page=6',
    prevPageUrl: `http://localhost:3333/users/10/payments?page=${i}`,
    nextPageUrl: `http://localhost:3333/users/10/payments?page=${i + 2}`,
  }));
  const currentPagePoint = Number(paramPage || 1);
  const targetPage = pages.find(
    (page) => page.currentPage === currentPagePoint,
  );

  const _parkings = [...Array(55).keys()].map((i) => {
    return {
      id: i + 1,
      name: `ID:${i + 1} 新宿若葉第${i + 1}`,
      address: '東京都新宿区１丁目４−１',
      isAppPaymentSupported: i % 2 === 0,
      tags: [{ name: 'ロック板式' }, { name: 'ゲート式' }],
      sectionCount: 10,
      otherSectionCount: 5,
      monthlySectionCount: choiceRandom([5, null]),
      latitude:
        i % 2
          ? defaultMapCenter.lat - 0.01 * i
          : defaultMapCenter.lat + 0.01 * i,
      longitude:
        i % 2
          ? defaultMapCenter.lng - 0.01 * i
          : defaultMapCenter.lng + 0.01 * i,
      hasAvailableRentalGarage: i % 3 === 0,
      isDummy: i % 5 === 0,
      priceSystems: [
        {
          title: '全曜日',
          timeZone: '8:00 ~ 20:00',
          unit: '30分',
          price: '200円',
          remarks: 'オールタイム',
        },
        {
          title: '全曜日',
          timeZone: '8:00 ~ 20:00',
          unit: '30分',
          price: '200円',
          remarks: '5番車室のみ',
        },
        {
          title: '24時間最大（日・祝）',
          timeZone: '8:00 ~ 20:00',
          unit: '', // unitは空文字の場合もある
          price: '200円',
          remarks: '15.16番車室のみ',
        },
      ],
    };
  });

  const paginationLimit = Number(req.url.searchParams.get('limit')) || 10;
  const pageLength = Math.ceil(_parkings.length / paginationLimit);

  const slicedParkings = [...Array(pageLength).keys()].map((i) =>
    _parkings.slice(i * paginationLimit, (i + 1) * paginationLimit),
  );

  return delayedResponse(
    ctx.status(200),
    ctx.json<{ parkings: Parking[]; pagination: Pagination }>({
      parkings: slicedParkings[Number(paramPage || 1) - 1],
      pagination: targetPage || pages[0],
    }),
  );
};

const getParkingDetail: ResponseResolver<RestRequest, typeof restContext> = (
  req,
  res,
  ctx,
) => {
  const parkingId = Number(req.params.parkingId);

  // テスト用のデータパターンを定義
  const getTestData = (id: number) => {
    switch (id) {
      case 1: // 時間貸し: オープン前の駐車場
        return {
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】オープン前駐車場（時間貸し）',
          note: '時間貸し検索結果に表示されない',
        };
      case 2: // 時間貸し: オープン日前の駐車場
        return {
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】オープン前駐車場（ナビHP公開ON）',
          note: '時間貸し検索結果に表示される',
        };
      case 3: // 時間貸し: オープン以降の駐車場で時間貸し検索結果に表示されない
        return {
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】時間貸し不可駐車場',
          note: '詳細ページ「時間貸しができない」旨の画面を表示する',
        };
      case 4: // 月極: 貸出区画がない駐車場
        return {
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】オープン前駐車場（月極）',
          note: '月極検索の結果に表示されない',
        };
      case 5: // 月極: 全ての貸出区画が公開日前の駐車場
        return {
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】月極検索結果に表示されない',
          note: '月極検索結果に表示されない',
        };
      case 6: // 月極: 全ての貸出区画が公開日前でも月極検索の結果に表示される
        return {
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】月極検索結果に表示される',
          note: '月極検索結果に表示される',
        };
      case 7: // 詳細: オープン前の駐車場（公開予定画面表示）
        return {
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】詳細:オープン前（公開予定）',
          note: '「公開予定」画面を表示する',
        };
      case 8: // 詳細: オープン前の駐車場（公開予定画面表示）
        return {
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】詳細:オープン済み',
          note: '「公開予定」画面を表示する',
        };
      case 9: // 詳細: オープン前かつ貸出可区画数が0の駐車場
        return {
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】詳細:オープン前・区画なし',
          note: '「時間貸しができない」旨の画面を表示する',
        };
      case 10: // 月極詳細: 貸出区画がない駐車場（月極契約ができない旨表示）
        return {
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】月極:契約できない',
          note: '「月極契約ができない」旨の画面を表示する',
        };
      case 11: // 月極詳細: 全ての貸出区画が公開日前の駐車場（月極契約ができない旨表示）
        return {
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】月極:全区画公開日前',
          note: '「月極契約ができない」旨の画面を表示する',
        };
      case 12: // 月極詳細: 公開日を過ぎた貸出区画がひとつでもある駐車場（詳細ページ表示）
        return {
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】月極:詳細ページ表示',
          note: '詳細ページを表示する',
        };
      default:
        return {
          isOpened: parkingId % 2 !== 0,
          publishDateTime:
            parkingId % 2 === 0
              ? '2025-12-31T09:00:00+09:00'
              : '2024-01-01T09:00:00+09:00',
          name: `新宿若葉第${parkingId}`,
          note: 'この駐車場はA側（ゲート）とB側（ロック板）に分かれております。',
        };
    }
  };

  const testData = getTestData(parkingId);

  // テスト中は常に成功レスポンスを返す
  return delayedResponse(
    ctx.status(200),
    ctx.json<{
      parking: ParkingDetail;
      passes?: Pass[];
      garages: Garage[];
    }>({
      parking: {
        id: parkingId,
        name: testData.name,
        address: '東京都新宿区１丁目４−１',
        isAppPaymentSupported: true,
        tags: [
          { name: 'ゲート式' },
          { name: 'ロック板式' },
          { name: 'ロックレス' },
          { name: '最大料金あり' },
          { name: 'カーシェアあり' },
          // { name: '駐車サービス券販売可' },
          { name: '駐車サービス券販売単位100枚' },
          { name: '駐車サービス券お急ぎ便選択不可' },
          { name: '夢なびポイントにてサービス券購入可' },
          // { name: 'ビジネスカード対応' },
          // { name: 'クレジット対応' },
          { name: '電子マネー対応' },
          { name: '夢なびポイント付与' },
          { name: '夢なび・dポイントアプリ利用可能' },
        ],
        openTime: '24時間',
        sectionCount: parkingId === 3 || parkingId === 9 ? 0 : 10,
        otherSectionCount: 5,
        sectionInformation:
          '高さ2.1m以下、長さ5m以下、幅1.9m以下、重量2.5t以下、車底15cm以上',
        isDeleted: false,
        note: testData.note,
        isOpened: testData.isOpened,
        publishDateTime: testData.publishDateTime,
        priceSystems: [
          {
            title: '全曜日',
            timeZone: '8:00 ~ 20:00',
            unit: '30分',
            price: '200円',
            remarks: 'オールタイム',
          },
          {
            title: '全曜日',
            timeZone: '8:00 ~ 20:00',
            unit: '30分',
            price: '200円',
            remarks: '5番車室のみ',
          },
          {
            title: '24時間最大（日・祝）',
            timeZone: '8:00 ~ 20:00',
            unit: '30分',
            price: '200円',
            remarks: '15.16番車室のみ',
          },
        ],
        latitude: 35.710063,
        longitude: 139.8107,
      },
      passes: [
        {
          id: 10,
          amount: 10000,
          availableDays: 30,
          validityPeriods: [
            {
              startDate: '2024-03-01T00:00:00+09:00',
              endDate: '2024-03-30T00:00:00+09:00',
              remain: 10,
            },
          ],
        },
      ],
      garages: [
        { id: 101, number: '1' },
        { id: 102, number: '2' },
        { id: 103, number: '3' },
      ],
    }),
  );
};

const getParkingSituation: ResponseResolver<RestRequest, typeof restContext> = (
  req,
  res,
  ctx,
) => {
  const garageId = Number(req.params.garageId);
  return delayedResponse(
    ctx.status(200),
    ctx.json<{ garage: Garage; usage: Usage }>({
      garage: { id: garageId, number: String(garageId) },
      usage: {
        currentTime: '2023-01-01T18:20:11+09:00',
        parkingName: '日野栄町第5駐車場',
        enteredAt: '2023-01-01T15:20:11+09:00',
        enteringMinutes: 339,
        paymentAmount: garageId * 10,
        isOverCumulativeMax: false,
      },
    }),
  );
};

const mockParking = {
  getAllPrefectures,
  getAllCitiesInThePrefecture,
  getAllParkingsInTheCity,
  searchByKeyword,
  searchByLocation,
  getParkingDetail,
  getParkingSituation,
};
export default mockParking;
